import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api, errorText } from '../lib/api';
import { cacheGet, cacheSet, listOps, localEntries, type LocalEntry, type OutboxOp } from '../lib/store';
import { captureEntry, createSessionOffline, deviceId, queueOp, syncEvents } from '../lib/sync';
import { classNames, fmtTime, go, TYPE_LABEL, useServerEvents } from '../lib/util';
import { Clarifications } from '../components/Clarifications';

type Tab = 'notes' | 'organized' | 'people' | 'threads' | 'questions';

export interface FeedEntry {
  id: string;
  text: string;
  revisionId: string;
  rewritten: string | null;
  rewriteFlags: string[];
  capturedAt: string;
  clientSeq: number;
  prefix: string | null;
  perspective: string | null;
  visibility: 'public' | 'gm_only' | null;
  inGameTime: string | null;
  aiStatus: string | null;
  aiError?: string | null;
  hasConflict?: boolean;
  kind?: string;
  local: 'local' | 'synced';
  pendingEdit?: boolean;
  pendingDelete?: boolean;
  failedSync?: string;
}

export function Dashboard({ campaignId, sessionId }: { campaignId: string; sessionId?: string }) {
  const [dash, setDash] = useState<any>(null);
  const [offline, setOffline] = useState(false);
  const [locals, setLocals] = useState<LocalEntry[]>([]);
  const [ops, setOps] = useState<OutboxOp[]>([]);
  const [tab, setTab] = useState<Tab>('notes');
  const [error, setError] = useState('');
  const cacheKey = `dash:${campaignId}:${sessionId ?? 'latest'}`;

  const load = useCallback(async () => {
    try {
      const d = await api(`/api/campaigns/${campaignId}/dashboard${sessionId ? `?session=${sessionId}` : ''}`);
      setDash(d);
      setOffline(false);
      setError('');
      void cacheSet(cacheKey, d);
      if (!sessionId && d.session) void cacheSet(`dash:${campaignId}:${d.session.id}`, d);
    } catch (e: any) {
      if (e?.status === 404 && sessionId) {
        // Session created offline and not synced yet: show campaign data plus local notes.
        try {
          const d = await api(`/api/campaigns/${campaignId}/dashboard`);
          setDash({ ...d, session: null, entries: [], notes: [], relevant: [] });
          setOffline(false);
        } catch {
          setDash((prev: any) => prev ?? { campaign: null, sessions: [], session: null, entries: [], notes: [], relevant: [], threads: [], clarifications: [] });
        }
        return;
      }
      const cached = await cacheGet<any>(cacheKey);
      if (cached) setDash(cached);
      setOffline(true);
      if (!cached) setError(errorText(e));
    }
  }, [campaignId, sessionId, cacheKey]);

  const currentSessionId: string | undefined = sessionId ?? dash?.session?.id;

  const loadLocal = useCallback(async () => {
    const all = await listOps();
    setOps(all.filter((o) => o.campaignId === campaignId));
    if (currentSessionId) setLocals(await localEntries(currentSessionId));
  }, [campaignId, currentSessionId]);

  useEffect(() => {
    setDash(null);
    (async () => {
      const cached = await cacheGet<any>(cacheKey);
      if (cached) setDash((d: any) => d ?? cached);
      await load();
    })();
  }, [cacheKey, load]);
  useEffect(() => {
    void loadLocal();
  }, [loadLocal]);
  useEffect(() => {
    const onOutbox = () => void loadLocal();
    const onSynced = () => {
      void loadLocal();
      void load();
    };
    syncEvents.addEventListener('outbox', onOutbox);
    syncEvents.addEventListener('synced', onSynced);
    return () => {
      syncEvents.removeEventListener('outbox', onOutbox);
      syncEvents.removeEventListener('synced', onSynced);
    };
  }, [load, loadLocal]);
  const reloadTimer = useRef<number>(0);
  useServerEvents(campaignId, () => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => void load(), 250);
  });
  useEffect(() => {
    const t = window.setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const campaign = dash?.campaign;
  const pendingSessions = ops.filter((o) => o.type === 'createSession').map((o) => ({ id: o.payload.id, title: o.payload.title, number: null, pending: true }));
  const sessions = [...pendingSessions.filter((p) => !dash?.sessions?.some((s: any) => s.id === p.id)), ...(dash?.sessions ?? [])];
  const session = dash?.session ?? sessions.find((s) => s.id === currentSessionId) ?? null;

  const feed: FeedEntry[] = useMemo(() => {
    const server: FeedEntry[] = (dash?.session?.id === currentSessionId ? dash?.entries ?? [] : []).map((e: any) => ({ ...e, local: 'synced' as const }));
    const ids = new Set(server.map((e) => e.id));
    const fromLocal: FeedEntry[] = locals
      .filter((l) => !ids.has(l.id))
      .map((l) => ({
        id: l.id,
        text: l.text,
        revisionId: l.revisionId,
        rewritten: null,
        rewriteFlags: [],
        capturedAt: l.capturedAt,
        clientSeq: l.clientSeq,
        prefix: null,
        perspective: null,
        visibility: l.visibility,
        inGameTime: l.inGameTime,
        aiStatus: l.synced ? 'queued' : null,
        local: l.synced ? ('synced' as const) : ('local' as const),
      }));
    const all = [...server, ...fromLocal];
    for (const op of ops) {
      const e = all.find((x) => x.id === (op.payload.entryId ?? op.payload.id));
      if (!e) continue;
      if (op.type === 'editEntry') {
        e.text = op.payload.text;
        e.revisionId = op.payload.revisionId;
        e.pendingEdit = true;
      }
      if (op.type === 'deleteEntry') e.pendingDelete = true;
      if (op.failed) e.failedSync = op.error;
    }
    return all.filter((e) => !e.pendingDelete).sort((a, b) => a.capturedAt.localeCompare(b.capturedAt) || a.clientSeq - b.clientSeq);
  }, [dash, locals, ops, currentSessionId]);

  const newSession = async () => {
    const n = (dash?.sessions?.[0]?.number ?? 0) + 1;
    const id = await createSessionOffline(campaignId, `Session ${n}`);
    go(`/c/${campaignId}/s/${id}`);
  };

  if (!dash && error) return <div className="page"><p className="error">{error}</p></div>;
  if (!dash) return <div className="page muted">Loading…</div>;

  const openQuestions = (dash.clarifications ?? []).filter((c: any) => c.status === 'open').length;
  const isGm = campaign?.my_role === 'gm';

  return (
    <div className={classNames('dashboard', `tab-${tab}`)}>
      <div className="session-bar">
        <label className="session-select">
          <span className="sr-only">Session</span>
          <select value={currentSessionId ?? ''} onChange={(e) => go(`/c/${campaignId}/s/${e.target.value}`)} disabled={sessions.length === 0}>
            {sessions.length === 0 && <option value="">No sessions yet</option>}
            {sessions.map((s: any) => (
              <option key={s.id} value={s.id}>
                {s.number ? `Session ${s.number}` : 'New session'}
                {s.title && s.title !== `Session ${s.number}` ? ` — ${s.title}` : ''}
                {s.pending ? ' (not synced)' : ''}
              </option>
            ))}
          </select>
        </label>
        <button className="btn" onClick={newSession}>
          New session
        </button>
        {session && !session.pending && <SessionMeta campaignId={campaignId} session={session} onSaved={load} />}
        {offline && <span className="pill sync-offline">Offline — showing saved copy</span>}
      </div>

      {!currentSessionId ? (
        <div className="empty">
          <p>Start a session to begin taking notes.</p>
          <button className="btn primary" onClick={newSession}>
            Start session 1
          </button>
        </div>
      ) : (
        <div className="dash-grid">
          <section className="panel panel-notes" aria-label="Notes">
            <h2 className="panel-title">Notes</h2>
            <Feed entries={feed} campaignId={campaignId} isGm={isGm} onChanged={load} />
            <NoteInput campaignId={campaignId} sessionId={currentSessionId} isGm={isGm} defaultVisibility={campaign?.default_visibility ?? 'public'} />
          </section>
          <section className="panel panel-organized" aria-label="Organized notes">
            <h2 className="panel-title">Organized notes</h2>
            <OrganizedNotes notes={dash.session?.id === currentSessionId ? dash.notes ?? [] : []} feed={feed} campaignId={campaignId} sessionId={currentSessionId} onChanged={load} />
          </section>
          <section className="panel panel-questions" aria-label="Clarifications">
            <h2 className="panel-title">
              Clarifications {openQuestions > 0 && <span className="count">{openQuestions}</span>}
            </h2>
            <Clarifications items={dash.clarifications ?? []} campaignId={campaignId} onChanged={load} offline={offline} />
          </section>
          <section className="panel panel-people" aria-label="Characters and NPCs">
            <h2 className="panel-title">Characters & NPCs</h2>
            <People items={dash.relevant ?? []} campaignId={campaignId} />
          </section>
          <section className="panel panel-threads" aria-label="Quests, clues and questions">
            <h2 className="panel-title">Quests, clues & questions</h2>
            <Threads items={dash.threads ?? []} campaignId={campaignId} onChanged={load} offline={offline} />
          </section>
        </div>
      )}

      <nav className="tabbar" aria-label="Dashboard panels">
        {(
          [
            ['notes', 'Notes'],
            ['organized', 'Organized'],
            ['people', 'People'],
            ['threads', 'Threads'],
            ['questions', 'Questions'],
          ] as [Tab, string][]
        ).map(([k, label]) => (
          <button key={k} className={classNames(tab === k && 'active')} aria-pressed={tab === k} onClick={() => setTab(k)}>
            {label}
            {k === 'questions' && openQuestions > 0 && <span className="count">{openQuestions}</span>}
          </button>
        ))}
      </nav>
    </div>
  );
}

function SessionMeta({ campaignId, session, onSaved }: { campaignId: string; session: any; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: session.title ?? '', playedOn: session.played_on ?? '', inGameStart: session.in_game_start ?? '', inGameEnd: session.in_game_end ?? '' });
  const [err, setErr] = useState('');
  useEffect(() => setF({ title: session.title ?? '', playedOn: session.played_on ?? '', inGameStart: session.in_game_start ?? '', inGameEnd: session.in_game_end ?? '' }), [session.id]);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api(`/api/campaigns/${campaignId}/sessions/${session.id}`, { method: 'PATCH', body: { ...f, inGameStart: f.inGameStart || null, inGameEnd: f.inGameEnd || null, playedOn: f.playedOn || null } });
      setOpen(false);
      onSaved();
    } catch (e2) {
      setErr(errorText(e2));
    }
  };
  return (
    <div className="popover-wrap">
      <button className="btn ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
        Session details
      </button>
      {open && (
        <form className="popover form-grid" onSubmit={save}>
          <label className="span2">
            Title
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </label>
          <label>
            Played on
            <input type="date" value={f.playedOn} onChange={(e) => setF({ ...f, playedOn: e.target.value })} />
          </label>
          <span />
          <label>
            In-game start (optional)
            <input value={f.inGameStart} onChange={(e) => setF({ ...f, inGameStart: e.target.value })} placeholder="e.g. 3rd of Frostfall" />
          </label>
          <label>
            In-game end (optional)
            <input value={f.inGameEnd} onChange={(e) => setF({ ...f, inGameEnd: e.target.value })} />
          </label>
          {err && <p className="error span2">{err}</p>}
          <div className="span2">
            <button className="btn primary">Save</button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---- Note input ------------------------------------------------------------------------

export function NoteInput({ campaignId, sessionId, isGm, defaultVisibility }: { campaignId: string; sessionId: string; isGm: boolean; defaultVisibility: 'public' | 'gm_only' }) {
  const [text, setText] = useState('');
  const [vis, setVis] = useState<'public' | 'gm_only'>(defaultVisibility);
  const [inGame, setInGame] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [err, setErr] = useState('');
  const composing = useRef(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setVis(defaultVisibility), [defaultVisibility, campaignId]);

  const submit = async () => {
    const value = text.trim();
    if (!value) return;
    // Clear immediately so typing can continue; restore only if the local save fails.
    setText('');
    setErr('');
    ref.current?.focus();
    try {
      await captureEntry({ campaignId, sessionId, text: value, visibility: isGm ? vis : null, inGameTime: inGame.trim() || null });
      if (navigator.storage?.persist) void navigator.storage.persist().catch(() => undefined);
    } catch (e) {
      setText((t) => (t ? `${value}\n${t}` : value));
      setErr(`Could not save locally: ${errorText(e)}`);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    // IME: never submit while a composition is active (Safari reports keyCode 229 on the final Enter).
    if (composing.current || e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    void submit();
  };

  return (
    <div className="note-input">
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <div className="note-input-row">
        <label htmlFor="note-text" className="sr-only">
          New note
        </label>
        <textarea
          id="note-text"
          ref={ref}
          value={text}
          rows={2}
          placeholder="Type a note… (Enter saves, Shift+Enter for a new line)"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onCompositionStart={() => (composing.current = true)}
          onCompositionEnd={() => setTimeout(() => (composing.current = false), 0)}
          enterKeyHint="send"
          autoComplete="off"
          spellCheck
        />
        <button className="btn primary submit-btn" onClick={() => void submit()} aria-label="Save note" disabled={!text.trim()}>
          Save
        </button>
      </div>
      <div className="note-input-opts">
        {isGm && (
          <button type="button" className={classNames('chip', vis === 'gm_only' ? 'chip-secret' : 'chip-public')} onClick={() => setVis(vis === 'gm_only' ? 'public' : 'gm_only')} aria-pressed={vis === 'gm_only'}>
            {vis === 'gm_only' ? 'GM only' : 'Public'}
          </button>
        )}
        <button type="button" className="chip" onClick={() => setShowMore(!showMore)} aria-expanded={showMore}>
          In-game time{inGame ? `: ${inGame}` : ''}
        </button>
        {showMore && (
          <input className="ingame" value={inGame} onChange={(e) => setInGame(e.target.value)} placeholder="optional, e.g. Day 3, dusk" aria-label="In-game time for next notes" />
        )}
        <span className="muted small hint">Enter saves · Shift+Enter new line</span>
      </div>
    </div>
  );
}

// ---- Feed ------------------------------------------------------------------------------

function Feed({ entries, campaignId, isGm, onChanged }: { entries: FeedEntry[]; campaignId: string; isGm: boolean; onChanged: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const lastCount = useRef(0);
  const [unseen, setUnseen] = useState(0);

  const onScroll = () => {
    const el = box.current!;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (atBottom.current) setUnseen(0);
  };
  // Only follow new notes when already at the bottom; never jump while reading.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const added = entries.length - lastCount.current;
    if (added > 0) {
      if (atBottom.current || lastCount.current === 0) el.scrollTop = el.scrollHeight;
      else setUnseen((u) => u + added);
    }
    lastCount.current = entries.length;
  }, [entries.length]);

  return (
    <div className="feed-wrap">
      <div className="feed" ref={box} onScroll={onScroll} aria-live="polite" aria-relevant="additions">
        {entries.length === 0 && <p className="muted empty-small">No notes in this session yet. Type below and press Enter.</p>}
        {entries.map((e) => (
          <EntryItem key={e.id} e={e} campaignId={campaignId} isGm={isGm} onChanged={onChanged} />
        ))}
      </div>
      {unseen > 0 && (
        <button
          className="btn new-below"
          onClick={() => {
            box.current!.scrollTop = box.current!.scrollHeight;
            setUnseen(0);
          }}
        >
          {unseen} new note{unseen === 1 ? '' : 's'} ↓
        </button>
      )}
    </div>
  );
}

const AI_LABEL: Record<string, string> = { queued: 'Queued for AI', processing: 'Processing', processed: 'Processed', failed: 'AI failed', skipped: 'Skipped' };

function EntryItem({ e, campaignId, isGm, onChanged }: { e: FeedEntry; campaignId: string; isGm: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(e.text);
  const [details, setDetails] = useState<any>(null);
  const [err, setErr] = useState('');
  const saveEdit = async () => {
    const t = draft.trim();
    if (!t || t === e.text) return setEditing(false);
    await queueOp('editEntry', campaignId, { entryId: e.id, revisionId: crypto.randomUUID(), baseRevisionId: e.revisionId, text: t, deviceId: deviceId() });
    setEditing(false);
  };
  const remove = async () => {
    if (!confirm('Delete this note? Organized records that depend only on it will be retracted.')) return;
    await queueOp('deleteEntry', campaignId, { entryId: e.id });
  };
  const act = async (fn: () => Promise<unknown>) => {
    setErr('');
    try {
      await fn();
      onChanged();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  const toggleDetails = async () => {
    if (details) return setDetails(null);
    try {
      setDetails(await api(`/api/entries/${e.id}/history`));
    } catch (x) {
      setErr(errorText(x));
    }
  };
  const syncLabel = e.failedSync ? 'Not synced' : e.local === 'local' || e.pendingEdit ? 'Saved locally' : 'Synced';
  return (
    <article className={classNames('entry', e.kind === 'clarification_answer' && 'entry-answer', e.visibility === 'gm_only' && isGm && 'entry-secret')}>
      <header className="entry-meta">
        <time dateTime={e.capturedAt}>{fmtTime(e.capturedAt)}</time>
        {e.prefix && <span className="tag">{e.prefix}</span>}
        {e.perspective && !e.prefix && <span className="tag subtle">{e.perspective}</span>}
        {isGm && e.visibility === 'gm_only' && <span className="tag secret">GM only</span>}
        {e.inGameTime && <span className="tag subtle">⏳ {e.inGameTime}</span>}
        <span className="status">
          <span className={classNames('st', e.failedSync ? 'st-bad' : e.local === 'local' || e.pendingEdit ? 'st-local' : 'st-ok')} title={e.failedSync}>
            {syncLabel}
          </span>
          {e.aiStatus && e.local === 'synced' && !e.pendingEdit && <span className={classNames('st', `ai-${e.aiStatus}`)}>{AI_LABEL[e.aiStatus] ?? e.aiStatus}</span>}
        </span>
      </header>
      {editing ? (
        <div className="entry-edit">
          <textarea value={draft} onChange={(x) => setDraft(x.target.value)} rows={3} autoFocus aria-label="Edit note" />
          <div className="row">
            <button className="btn primary small" onClick={() => void saveEdit()}>
              Save edit
            </button>
            <button className="btn small" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="entry-text">{e.text}</p>
      )}
      {e.rewritten && !editing && <p className="entry-rewrite">{e.rewritten}</p>}
      {e.rewriteFlags?.length > 0 && <p className="warn small">⚠ {e.rewriteFlags.join('; ')}</p>}
      {e.hasConflict && <p className="warn small">Edited on two devices — resolve under Clarifications.</p>}
      {e.aiStatus === 'failed' && (
        <p className="error small">
          {e.aiError ?? 'AI processing failed.'}{' '}
          <button className="link" onClick={() => void act(() => api(`/api/entries/${e.id}/retry`, { body: {} }))}>
            Retry
          </button>
        </p>
      )}
      {err && <p className="error small">{err}</p>}
      {!editing && (
        <div className="entry-actions">
          <button className="link" onClick={() => { setDraft(e.text); setEditing(true); }}>
            Edit
          </button>
          <button className="link" onClick={() => void remove()}>
            Delete
          </button>
          {e.local === 'synced' && (
            <button className="link" onClick={() => void toggleDetails()} aria-expanded={!!details}>
              {details ? 'Hide details' : 'Details'}
            </button>
          )}
          {isGm && e.local === 'synced' && (
            <button className="link" onClick={() => void act(() => api(`/api/entries/${e.id}/visibility`, { body: { visibility: e.visibility === 'gm_only' ? 'public' : 'gm_only' } }))}>
              Make {e.visibility === 'gm_only' ? 'public' : 'GM only'}
            </button>
          )}
          {e.local === 'synced' && (
            <button
              className="link"
              onClick={() => {
                const v = prompt('In-game date/time for this note (leave empty to clear):', e.inGameTime ?? '');
                if (v !== null) void act(() => api(`/api/entries/${e.id}/in-game-time`, { body: { inGameTime: v } }));
              }}
            >
              In-game time
            </button>
          )}
        </div>
      )}
      {details && (
        <div className="entry-details">
          <h4>Revisions</h4>
          <ol>
            {details.revisions.map((r: any) => (
              <li key={r.id}>
                <span className="muted small">
                  {fmtTime(r.created_at)} · {r.source}
                  {r.status !== 'accepted' ? ` · ${r.status}` : ''}
                </span>
                <div>{r.text}</div>
              </li>
            ))}
          </ol>
          <h4>Derived from this note</h4>
          {details.derived.claims.length + details.derived.notes.length + details.derived.entities.length === 0 && <p className="muted small">Nothing yet.</p>}
          <ul>
            {details.derived.entities.map((x: any) => (
              <li key={x.id}>
                <a href={`#/c/${campaignId}/records/e/${x.id}`}>{x.name}</a> <span className="muted small">({TYPE_LABEL[x.type]}, {x.origin === 'alias' ? 'name match' : 'AI'})</span>
              </li>
            ))}
            {details.derived.claims.map((x: any) => (
              <li key={x.id}>
                <span className="tag subtle">{x.kind}</span> {x.statement} {x.status !== 'active' && <span className="muted small">({x.status})</span>}
              </li>
            ))}
            {details.derived.notes.map((x: any) => (
              <li key={x.id}>
                <span className="tag subtle">{x.heading}</span> {x.text} {x.status !== 'active' && <span className="muted small">({x.status})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

// ---- Organized notes ---------------------------------------------------------------------

function OrganizedNotes({ notes, feed, campaignId, sessionId, onChanged }: { notes: any[]; feed: FeedEntry[]; campaignId: string; sessionId: string; onChanged: () => void }) {
  const groups = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const n of notes) {
      if (!m.has(n.heading)) m.set(n.heading, []);
      m.get(n.heading)!.push(n);
    }
    return [...m.entries()];
  }, [notes]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ heading: '', text: '' });
  const [err, setErr] = useState('');
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api(`/api/campaigns/${campaignId}/sessions/${sessionId}/notes`, { body: form });
      setForm({ heading: form.heading, text: '' });
      setAdding(false);
      onChanged();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  const pending = feed.filter((e) => e.aiStatus !== 'processed').length;
  return (
    <div className="organized">
      {groups.length === 0 && <p className="muted empty-small">{feed.length ? 'Organized notes appear here after AI processing.' : 'Nothing yet.'}</p>}
      {pending > 0 && groups.length > 0 && <p className="muted small">{pending} note{pending === 1 ? '' : 's'} not organized yet.</p>}
      {groups.map(([heading, items]) => (
        <section key={heading} className="org-group">
          <h3>{heading}</h3>
          <ul>
            {items.map((n) => (
              <NoteItem key={n.id} n={n} feed={feed} campaignId={campaignId} onChanged={onChanged} />
            ))}
          </ul>
        </section>
      ))}
      {adding ? (
        <form className="form-grid compact" onSubmit={add}>
          <label>
            Heading
            <input value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} required list="note-headings" />
            <datalist id="note-headings">
              {groups.map(([h]) => (
                <option key={h} value={h} />
              ))}
            </datalist>
          </label>
          <label className="span2">
            Note
            <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required rows={2} />
          </label>
          {err && <p className="error span2">{err}</p>}
          <div className="span2 row">
            <button className="btn primary small">Add</button>
            <button type="button" className="btn small" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button className="link" onClick={() => setAdding(true)}>
          + Add organized note
        </button>
      )}
    </div>
  );
}

function NoteItem({ n, feed, campaignId, onChanged }: { n: any; feed: FeedEntry[]; campaignId: string; onChanged: () => void }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(n.text);
  const [showSrc, setShowSrc] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    try {
      await api(`/api/campaigns/${campaignId}/notes/${n.id}`, { method: 'PATCH', body: { text } });
      setEdit(false);
      onChanged();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  const srcs = (n.sources ?? []).map((id: string) => feed.find((f) => f.id === id)).filter(Boolean) as FeedEntry[];
  return (
    <li className={classNames('org-item', `st-${n.status}`)}>
      {edit ? (
        <div className="entry-edit">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} aria-label="Edit organized note" />
          <div className="row">
            <button className="btn primary small" onClick={() => void save()}>
              Save
            </button>
            <button className="btn small" onClick={() => setEdit(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <span>{n.text}</span>
      )}
      {n.status === 'disputed' && <span className="tag warn-tag">disputed</span>}
      {n.status === 'superseded' && <span className="tag subtle">superseded</span>}
      {n.origin === 'user' || n.user_edited_at ? <span className="tag subtle">edited by you</span> : null}
      {!edit && (
        <span className="item-actions">
          {srcs.length > 0 && (
            <button className="link small" onClick={() => setShowSrc(!showSrc)} aria-expanded={showSrc}>
              {srcs.length} source{srcs.length === 1 ? '' : 's'}
            </button>
          )}
          <button className="link small" onClick={() => { setText(n.text); setEdit(true); }}>
            Edit
          </button>
        </span>
      )}
      {showSrc && (
        <ul className="sources">
          {srcs.map((s) => (
            <li key={s.id}>
              <time className="muted small">{fmtTime(s.capturedAt)}</time> {s.text}
            </li>
          ))}
        </ul>
      )}
      {err && <p className="error small">{err}</p>}
    </li>
  );
}

// ---- People / threads --------------------------------------------------------------------

function People({ items, campaignId }: { items: any[]; campaignId: string }) {
  if (items.length === 0) return <p className="muted empty-small">Characters, NPCs, places and items mentioned this session show up here.</p>;
  const order = ['pc', 'npc', 'faction', 'location', 'item'];
  const sorted = [...items].sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
  return (
    <ul className="people">
      {sorted.map((p) => (
        <li key={p.id}>
          <a href={`#/c/${campaignId}/records/e/${p.id}`}>
            <span className={classNames('type', `type-${p.type}`)}>{TYPE_LABEL[p.type]}</span>
            <span className="name">{p.name}</span>
          </a>
          {p.description && <p className="muted small clamp">{p.description}</p>}
        </li>
      ))}
    </ul>
  );
}

function Threads({ items, campaignId, onChanged, offline }: { items: any[]; campaignId: string; onChanged: () => void; offline: boolean }) {
  const [err, setErr] = useState('');
  const toggleObjective = async (t: any, idx: number) => {
    const objectives = t.objectives.map((o: any, i: number) => (i === idx ? { ...o, done: !o.done } : o));
    try {
      await api(`/api/campaigns/${campaignId}/entities/${t.id}`, { method: 'PATCH', body: { objectives } });
      onChanged();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  if (items.length === 0) return <p className="muted empty-small">Quests, clues and open questions appear here.</p>;
  const groups: [string, string][] = [
    ['quest', 'Quests'],
    ['clue', 'Clues'],
    ['question', 'Unanswered questions'],
  ];
  return (
    <div className="threads">
      {err && <p className="error small">{err}</p>}
      {groups.map(([type, label]) => {
        const list = items.filter((t) => t.type === type);
        if (!list.length) return null;
        return (
          <section key={type}>
            <h3>{label}</h3>
            <ul>
              {list.map((t) => (
                <li key={t.id} className={classNames(!['open', 'active'].includes(t.status) && 'closed')}>
                  <a href={`#/c/${campaignId}/records/e/${t.id}`}>{t.name}</a>
                  {t.status && !['open', 'active'].includes(t.status) && <span className="tag subtle">{t.status}</span>}
                  {t.sessionMentions > 0 && <span className="tag subtle">this session</span>}
                  {t.objectives?.length > 0 && (
                    <ul className="objectives">
                      {t.objectives.map((o: any, i: number) => (
                        <li key={o.id ?? i}>
                          <label>
                            <input type="checkbox" checked={!!o.done} disabled={offline} onChange={() => void toggleObjective(t, i)} /> {o.text}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
